import {
  demographicSurveyReportMiddleware,
  hiringReportMiddleware,
  newReportsMiddleware,
  routingFromHiringReport,
} from '@/helpers/routeMiddleware';
import { handleRedirectToLogin } from '@/routes/helpers';
import ComplianceReport from '@/components/insights/ActionableInsight/ComplianceReport.vue';
import JobPerformanceReport from '@/components/insights/ActionableInsight/JobPerformanceReport.vue';
import EmployerBrandReport from '@/components/insights/ActionableInsight/EmployerBrandReport.vue';
import ContentSnapshot from '@/components/insights/OperationalInsights/ContentSnapshot/ContentSnapshot.vue';
import LanguageSnapshot from '@/components/insights/OperationalInsights/LanguageSnapshot/LanguageSnapshot.vue';
import RecruitingPerformance from '@/components/insights/OperationalInsights/RecruitingPerformance/RecruitingPerformance.vue';
import Usage from '@/components/insights/OperationalInsights/Usage/Usage.vue';
import WeeklySnapshot from '@/components/insights/OperationalInsights/WeeklySnapshot/WeeklySnapshot.vue';
import DemographicSurveyReport from '@/components/insights/ProcessInsights/DemographicSurveyReport/DemographicSurveyReport.vue';
import DropoffReport from '@/components/insights/ProcessInsights/DropoffReport/DropoffReport.vue';
import ExecutiveInsightsReport from '@/components/insights/ProcessInsights/ExecutiveInsightsReport/ExecutiveInsightsReport.vue';
import GenderReport from '@/components/insights/ProcessInsights/GenderReport/GenderReport.vue';
import HiringReport from '@/components/insights/ProcessInsights/HiringReport/HiringReport.vue';
import PassthroughReport from '@/components/insights/ProcessInsights/PassthroughReport/PassthroughReport.vue';
import InboundReport from '@/components/insights/SourceInsights/InboundReport/InboundReport.vue';
import OutboundReport from '@/components/insights/SourceInsights/OutboundReport/OutboundReport.vue';
import OverallReport from '@/components/insights/SourceInsights/OverallReport/OverallReport.vue';
import Insights from '@/views/Insights.vue';

export const insights = (authDataStore) => ([
  {
    path: '/insights/',
    name: 'insights',
    component: Insights,
    redirect: { name: 'recruiting-performance' },
    meta: { title: 'Insights' },
    children: [
      {
        name: 'executive-insights',
        path: 'executive-insights',
        component: ExecutiveInsightsReport,
        beforeEnter: newReportsMiddleware(authDataStore),
        meta: { title: 'Executive Insights' },
      },
      {
        name: 'demographic-survey-report',
        path: 'demographic-survey-report',
        component: DemographicSurveyReport,
        beforeEnter: demographicSurveyReportMiddleware(authDataStore),
        meta: { title: 'Demographic Survey Report' },
      },
      {
        name: 'overall-report',
        path: 'overall-report',
        component: OverallReport,
        beforeEnter: newReportsMiddleware(authDataStore),
        meta: { title: 'Overall Report' },
      },
      {
        name: 'inbound-report',
        path: 'inbound-report',
        component: InboundReport,
        beforeEnter: newReportsMiddleware(authDataStore),
        meta: { title: 'Inbound Report' },
      },
      {
        name: 'outbound-report',
        path: 'outbound-report',
        component: OutboundReport,
        beforeEnter: newReportsMiddleware(authDataStore),
        meta: { title: 'Outbound Report' },
      },
      {
        name: 'gender-report',
        path: 'gender-report',
        component: GenderReport,
        beforeEnter: newReportsMiddleware(authDataStore),
        meta: { title: 'Gender Report' },
      },
      {
        name: 'drop-off',
        path: 'drop-off',
        component: DropoffReport,
        beforeEnter: newReportsMiddleware(authDataStore),
        meta: { title: 'Drop-off Report' },
      },
      {
        name: 'pass-through',
        path: 'pass-through',
        component: PassthroughReport,
        beforeEnter: newReportsMiddleware(authDataStore),
        meta: { title: 'Pass-through Report' },
      },
      {
        name: 'compliance-report',
        path: 'compliance-report',
        component: ComplianceReport,
        meta: { authStateRequired: 'either', title: 'Compliance Report' },
        beforeEnter: async (to, from, next) => {
          if (!window?.sessionStorage?.reportingShareKey && !to.query.key) {
            await handleRedirectToLogin(authDataStore, to, next);
            next();
          } else {
            next();
          }
        },
      },
      {
        name: 'employer-brand-report',
        path: 'employer-brand-report',
        component: EmployerBrandReport,
        meta: { authStateRequired: 'either', title: 'Employer Brand Report' },
        beforeEnter: async (to, from, next) => {
          if (!window?.sessionStorage?.reportingShareKey && !to.query.key) {
            await handleRedirectToLogin(authDataStore, to, next);
            next();
          } else {
            next();
          }
        },
      },
      {
        name: 'easy-wins-report',
        path: 'easy-wins-report',
        redirect: (to) => ({ name: 'employer-brand-report', query: to.query }),
      },
      {
        name: 'confidence-gap-report',
        path: 'confidence-gap-report',
        redirect: (to) => ({ name: 'job-performance-report', query: to.query }),
      },
      {
        name: 'job-performance-report',
        path: 'job-performance-report',
        component: JobPerformanceReport,
        meta: { authStateRequired: 'either', title: 'Job Performance Report' },
        beforeEnter: async (to, from, next) => {
          if (!window?.sessionStorage?.reportingShareKey && !to.query.key) {
            await handleRedirectToLogin(authDataStore, to, next);
            next();
          } else {
            next();
          }
        },
      },
      {
        name: 'hiring-report',
        path: 'hiring-report',
        component: HiringReport,
        beforeEnter: hiringReportMiddleware(authDataStore),
        meta: { title: 'Hiring Report' },
      },
      {
        name: 'weekly-snapshot',
        path: 'weekly-snapshot',
        component: WeeklySnapshot,
        meta: { title: 'Weekly Snapshot Report' },
      },
      {
        name: 'recruiting-performance',
        path: 'recruiting-performance',
        component: RecruitingPerformance,
        meta: { authStateRequired: 'either', title: 'Recruiting Performance Report' },
        beforeEnter: async (to, from, next) => {
          if (!window?.sessionStorage?.reportingShareKey && !to.query.key) {
            await handleRedirectToLogin(authDataStore, to, next);
            routingFromHiringReport({ to, from });
            next();
          } else {
            next();
          }
        },
      },
      {
        name: 'source-performance',
        path: 'source-performance',
        redirect: (to) => ({ name: 'overall-report', query: to.query }),
        meta: { title: 'Source Performance' },
      },
      {
        name: 'language-snapshot',
        path: 'language-snapshot',
        component: LanguageSnapshot,
        meta: { authStateRequired: 'either', title: 'Language Snapshot' },
        beforeEnter: async (to, from, next) => {
          if (!window?.sessionStorage?.reportingShareKey && !to.query.key) {
            await handleRedirectToLogin(authDataStore, to, next);
            next();
          } else {
            next();
          }
        },
      },
      {
        name: 'content-snapshot',
        path: 'content-snapshot',
        component: ContentSnapshot,
        meta: { authStateRequired: 'either', title: 'Content Snapshot' },
        beforeEnter: async (to, from, next) => {
          if (!window?.sessionStorage?.reportingShareKey && !to.query.key) {
            await handleRedirectToLogin(authDataStore, to, next);
            next();
          } else {
            next();
          }
        },
      },
      {
        name: 'usage',
        path: 'usage',
        component: Usage,
        meta: { title: 'Usage Report' },
      },
    ],
  },
]);
